import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useSystemConfig } from '../../hooks/useSystemConfig';
import {
  CreditCard,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Loader2,
  Building2,
  ArrowRight,
  Shield,
  Banknote,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';

interface ConnectStatus {
  connected: boolean;
  status: string;
  accountId?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
}

export const AdminPaymentsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { currentBusiness } = useBusiness();
  const { config: systemConfig } = useSystemConfig(); // Configuración global del sistema
  const [loading, setLoading] = useState(true);
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const functions = getFunctions();

  useEffect(() => {
    if (currentBusiness?.id) {
      checkConnectStatus();
    }
  }, [currentBusiness?.id]);

  const checkConnectStatus = async () => {
    if (!currentBusiness?.id) return;

    setLoading(true);
    setError(null);

    try {
      const getStatus = httpsCallable(functions, 'getConnectAccountStatus');
      const result = await getStatus({ businessId: currentBusiness.id });
      setConnectStatus(result.data as ConnectStatus);
    } catch (err: any) {
      console.error('Error checking connect status:', err);
      // Si no hay cuenta, mostramos estado inicial
      setConnectStatus({ connected: false, status: 'not_created' });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    if (!currentBusiness?.id) return;

    setConnecting(true);
    setError(null);

    try {
      const createAccount = httpsCallable(functions, 'createConnectAccount');
      const result: any = await createAccount({
        businessId: currentBusiness.id,
        returnUrl: `${window.location.origin}/admin/payments?success=true`,
        refreshUrl: `${window.location.origin}/admin/payments?refresh=true`,
      });

      if (result.data.onboardingUrl) {
        window.location.href = result.data.onboardingUrl;
      } else if (result.data.alreadyConnected) {
        setConnectStatus({
          connected: true,
          status: 'active',
          accountId: result.data.accountId,
          chargesEnabled: true,
        });
      }
    } catch (err: any) {
      console.error('Error connecting Stripe:', err);
      setError(err.message || 'Error al conectar con Stripe');
    } finally {
      setConnecting(false);
    }
  };

  const handleOpenDashboard = async () => {
    if (!currentBusiness?.id) return;

    try {
      const createLink = httpsCallable(functions, 'createStripeDashboardLink');
      const result: any = await createLink({ businessId: currentBusiness.id });

      if (result.data.url) {
        window.open(result.data.url, '_blank');
      }
    } catch (err: any) {
      console.error('Error opening dashboard:', err);
      setError('Error al abrir el panel de Stripe');
    }
  };

  // Verificar parámetros de URL al cargar
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      checkConnectStatus();
    }
  }, []);

  const hasOnlinePayments = React.useMemo(() => {
    const plan = (currentBusiness?.subscription?.plan || 'free') as 'free' | 'basic' | 'pro';
    return systemConfig.plans[plan]?.hasOnlinePayments ?? false;
  }, [currentBusiness, systemConfig]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!hasOnlinePayments) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-6">
        <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
          <Shield className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Pagos Online No Disponibles</h2>
        <p className="text-gray-400 max-w-md mb-6">
          La recepción de pagos online a través de Stripe no está disponible en tu plan actual.
          Actualiza tu plan para empezar a cobrar a tus clientes automáticamente.
        </p>
        <Button
          onClick={() => window.location.href = '/admin/settings'}
          className="bg-gradient-to-r from-amber-500 to-orange-500"
        >
          Ver Planes
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <CreditCard className="w-7 h-7 text-amber-500" />
            Configuración de Pagos
          </h1>
          <p className="text-gray-400 mt-1">
            Conecta tu cuenta bancaria para recibir pagos de tus clientes
          </p>
        </div>
        <Button
          variant="outline"
          onClick={checkConnectStatus}
          className="gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-300">{error}</p>
        </div>
      )}

      {/* Estado de conexión */}
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${connectStatus?.connected ? 'bg-green-500/20' : 'bg-amber-500/20'}`}>
            {connectStatus?.connected ? (
              <CheckCircle className="w-8 h-8 text-green-400" />
            ) : (
              <Building2 className="w-8 h-8 text-amber-400" />
            )}
          </div>

          <div className="flex-1">
            <h2 className="text-xl font-bold text-white mb-1">
              {connectStatus?.connected ? 'Cuenta Conectada' : 'Stripe Connect'}
            </h2>
            <p className="text-gray-400 mb-4">
              {connectStatus?.connected
                ? 'Tu cuenta de Stripe está activa y lista para recibir pagos.'
                : 'Conecta tu cuenta bancaria con Stripe para empezar a recibir pagos de tus clientes.'}
            </p>

            {connectStatus?.connected ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <div className="text-sm text-gray-400">Estado de cargos</div>
                    <div className="flex items-center gap-2 mt-1">
                      {connectStatus.chargesEnabled ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          <span className="text-green-400 font-medium">Activo</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 text-amber-400" />
                          <span className="text-amber-400 font-medium">Pendiente</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <div className="text-sm text-gray-400">Estado de pagos</div>
                    <div className="flex items-center gap-2 mt-1">
                      {connectStatus.payoutsEnabled ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          <span className="text-green-400 font-medium">Activo</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 text-amber-400" />
                          <span className="text-amber-400 font-medium">Pendiente</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={handleOpenDashboard} className="gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Abrir Panel de Stripe
                  </Button>
                  {!connectStatus.chargesEnabled && (
                    <Button
                      variant="outline"
                      onClick={handleConnectStripe}
                      disabled={connecting}
                    >
                      {connecting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      Completar verificación
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <Button
                onClick={handleConnectStripe}
                disabled={connecting}
                className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
                Conectar con Stripe
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Información sobre Stripe Connect */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="p-2 bg-blue-500/20 rounded-lg w-fit mb-3">
            <Shield className="w-5 h-5 text-blue-400" />
          </div>
          <h3 className="font-semibold text-white mb-2">Pagos Seguros</h3>
          <p className="text-sm text-gray-400">
            Stripe es líder mundial en procesamiento de pagos con seguridad de nivel bancario.
          </p>
        </Card>

        <Card className="p-5">
          <div className="p-2 bg-green-500/20 rounded-lg w-fit mb-3">
            <Banknote className="w-5 h-5 text-green-400" />
          </div>
          <h3 className="font-semibold text-white mb-2">Cobros Directos</h3>
          <p className="text-sm text-gray-400">
            El dinero de tus clientes va directamente a tu cuenta bancaria.
          </p>
        </Card>

        <Card className="p-5">
          <div className="p-2 bg-purple-500/20 rounded-lg w-fit mb-3">
            <CreditCard className="w-5 h-5 text-purple-400" />
          </div>
          <h3 className="font-semibold text-white mb-2">Sin Complicaciones</h3>
          <p className="text-sm text-gray-400">
            Acepta tarjetas de crédito, débito y más métodos de pago.
          </p>
        </Card>
      </div>

      {/* Comisiones */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Comisiones</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-800">
            <span className="text-gray-400">Comisión de Stripe</span>
            <span className="text-white font-medium">1.4% + 0.25€ (tarjetas europeas)</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-800">
            <span className="text-gray-400">Comisión de la plataforma</span>
            <span className="text-white font-medium">{systemConfig.platformFeePercent}%</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-400">Ejemplo: Bono de 50€</span>
            <span className="text-green-400 font-medium">
              Recibes ~{(50 - (50 * 0.014) - 0.25 - (50 * systemConfig.platformFeePercent / 100)).toFixed(2)}€
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminPaymentsPage;



















